import { useState } from "react";
import type { AssignableRole } from "@/shared/organizations/access-client";
import { alreadyMemberConflict } from "@/shared/organizations/access-errors";
import { summarizeAssignmentResults } from "@/shared/organizations/assignment-results";
import { ORGANIZATION_LOCATIONS_MAX } from "@/shared/organizations/location-limits";
import type { AdminMembership } from "@/shared/organizations/membership-types";
import { useGoneLocations, withoutIds } from "@/shared/organizations/reconcile-locations";
import { withToggled } from "@/shared/organizations/toggle-set";
import {
  useAddOrganizationMember,
  useAssignMemberLocations,
} from "@/shared/organizations/use-access-mutations";
import { useAccessSubmit } from "@/shared/organizations/use-access-submit";
import type { OrganizationOption } from "@/shared/organizations/use-organization-search";
import {
  deriveAddToOrganizationView,
  type AddConflict,
  type AddToOrganizationView,
} from "./add-to-organization-view";

/** `assignedNoChange`: the API had nothing to do (the locations were already there). */
export type AddToOrganizationOutcome = "added" | "assigned" | "assignedNoChange";

interface Options {
  readonly userId: string;
  readonly memberships: readonly AdminMembership[];
  readonly canGrantAdmin: boolean;
  readonly onDone: (outcome: AddToOrganizationOutcome) => void;
  readonly onOpenChange: (open: boolean) => void;
}

/** The form state and the two write paths (add a member / assign more locations). */
export function useAddToOrganization({
  userId,
  memberships,
  canGrantAdmin,
  onDone,
  onOpenChange,
}: Options) {
  const add = useAddOrganizationMember();
  const assign = useAssignMemberLocations();
  const [organization, setOrganization] = useState<OrganizationOption | null>(null);
  const goneLocations = useGoneLocations(organization?.id ?? "");
  const [chosenRole, setChosenRole] = useState<AssignableRole>("STAFF");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [conflict, setConflict] = useState<AddConflict | null>(null);

  const view: AddToOrganizationView = deriveAddToOrganizationView({
    memberships,
    organizationId: organization?.id ?? null,
    conflict,
    chosenRole,
    canGrantAdmin,
  });
  const { role, existing, assigningMore, blocked } = view;
  const isPending = add.isPending || assign.isPending;
  const canSubmit =
    organization !== null &&
    !blocked &&
    !isPending &&
    (role === "ADMIN" || (selected.size > 0 && selected.size <= ORGANIZATION_LOCATIONS_MAX));

  const run = async (): Promise<AddToOrganizationOutcome> => {
    if (!organization) throw new Error("No organization picked");
    goneLocations.snapshot();
    const ids = [...selected];
    if (assigningMore && existing) {
      const result = await assign.mutateAsync({
        organizationId: organization.id,
        membershipId: existing.id,
        add: ids,
      });
      return summarizeAssignmentResults(result.results) === "changed"
        ? "assigned"
        : "assignedNoChange";
    }
    await add.mutateAsync({
      organizationId: organization.id,
      userId,
      role,
      ...(role === "STAFF" ? { locationIds: ids } : {}),
    });
    return "added";
  };

  const access = useAccessSubmit<AddToOrganizationOutcome>({
    run,
    isPending,
    canSubmit,
    onSuccess: (outcome) => {
      onDone(outcome);
      onOpenChange(false);
    },
    onOpenChange,
    onFailure: (err) => {
      const existingMember = alreadyMemberConflict(err);
      if (!existingMember || !organization) return false;
      setConflict({ organizationId: organization.id, role: existingMember.role });
      return true;
    },
    onLocationGone: async () => {
      // The write refreshed the lists already: drop only the ticks that vanished.
      const gone = await goneLocations.find();
      setSelected((prev) => withoutIds(prev, gone));
    },
  });

  return {
    ...view,
    organization,
    selected,
    isPending,
    canSubmit,
    access,
    chooseOrganization: (next: OrganizationOption | null) => {
      setOrganization(next);
      setSelected(new Set());
      setConflict(null);
      access.clearError();
    },
    chooseRole: (next: AssignableRole) => {
      setChosenRole(next);
      setSelected(new Set());
    },
    toggle: (id: string) => setSelected((prev) => withToggled(prev, id)),
  };
}
